import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Badge,
  Separator,
  Textarea,
} from '@jira-clone/ui'
import { useHsnSacCodes, useHsnSacCodeMutations } from '../../shared/hooks/useHsnSacCodes'
import { useTaxMasters, taxLabel } from '../../shared/hooks/useTaxMasters'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const TYPE_LABELS: Record<string, string> = {
  HSN: 'HSN (Goods)',
  SAC: 'SAC (Services)',
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  HSN: <Tag className="h-3.5 w-3.5" />,
  SAC: <Tag className="h-3.5 w-3.5" />,
}

const TYPE_COLORS: Record<string, string> = {
  HSN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  SAC: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800',
}

interface DraftState {
  id?: string
  code: string
  description: string
  type: 'HSN' | 'SAC'
  taxMasterId: string
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  code: '',
  description: '',
  type: 'HSN',
  taxMasterId: '',
  isActive: true,
}

export default function HsnSacAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const typeParam = searchParams.get('type') as 'HSN' | 'SAC' | 'all' | null
  const initialType = typeParam && ['HSN', 'SAC', 'all'].includes(typeParam) ? typeParam : 'all'
  const [activeType, setActiveTypeState] = useState(initialType)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  useEffect(() => {
    if (typeParam && ['HSN', 'SAC', 'all'].includes(typeParam)) {
      setActiveTypeState(typeParam)
    } else if (!typeParam) {
      setActiveTypeState('all')
    }
  }, [typeParam])

  const handleTypeChange = (type: 'HSN' | 'SAC' | 'all') => {
    setActiveTypeState(type)
    setSearchParams(type === 'all' ? {} : { type })
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value, ...(activeType !== 'all' ? { type: activeType } : {}) } : 
      activeType !== 'all' ? { type: activeType } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? { ...(activeType !== 'all' ? { type: activeType } : {}) } : 
      { isActive: value, ...(activeType !== 'all' ? { type: activeType } : {}) })
  }

  const { data: hsnSacCodes = [], isLoading } = useHsnSacCodes({
    type: activeType === 'all' ? undefined : activeType,
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
    search: search || undefined,
  })
  
  const { create, update, setStatus, remove } = useHsnSacCodeMutations()
  const { data: taxMasters = [] } = useTaxMasters({ isActive: true })

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (code: any) => {
    setDraft({
      id: code.id,
      code: code.code,
      description: code.description,
      type: code.type,
      taxMasterId: code.taxMasterId || '',
      isActive: code.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.code.trim()) {
      toast.error('Code is required.')
      return
    }
    if (!draft.description.trim()) {
      toast.error('Description is required.')
      return
    }
    try {
      const payload = {
        code: draft.code.trim().toUpperCase(),
        description: draft.description.trim(),
        type: draft.type,
        taxMasterId: draft.taxMasterId || null,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('HSN/SAC code updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('HSN/SAC code created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this code' : 'create this code'))
    }
  }

  const handleDelete = async (code: any) => {
    if (!confirm(`Delete "${code.code}"? This can't be undone.`)) return
    setDeletingId(code.id)
    try {
      await remove.mutateAsync(code.id)
      toast.success('HSN/SAC code deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this code'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (code: any) => {
    try {
      await setStatus.mutateAsync({ id: code.id, isActive: !code.isActive })
      toast.success(code.isActive ? 'Code deactivated.' : 'Code activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle code status'))
    }
  }

  const types: ('all' | 'HSN' | 'SAC')[] = ['all', 'HSN', 'SAC']

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">HSN / SAC Codes</h2>
          <p className="text-sm text-muted-foreground">Manage HSN (Harmonized System Nomenclature) and SAC (Services Accounting Code) codes.</p>
        </div>
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> New Code
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Type Tabs */}
            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
              {types.map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeType === type
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {type !== 'all' && <span style={{ color: type === 'HSN' ? '#3b82f6' : '#a855f7' }}>{TYPE_ICONS[type]}</span>}
                  {type === 'all' ? 'All' : TYPE_LABELS[type]}
                </button>
              ))}
            </div>

            <Separator className="h-6 w-px" />

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search codes..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : hsnSacCodes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No HSN/SAC codes yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {hsnSacCodes.map((code) => (
            <Card key={code.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-medium shrink-0 border ${TYPE_COLORS[code.type]}`}
                      >
                        {TYPE_ICONS[code.type]}
                      </span>
                      <Badge variant={code.isActive ? 'default' : 'secondary'} className="text-xs">
                        {code.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate font-mono">{code.code}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                        {code.description && <span>{code.description}</span>}
                        {code.taxMaster && <span>Tax: {code.taxMaster.name} ({code.taxMaster.rate}%)</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(code)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(code)}
                      title={code.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {code.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(code)}
                      disabled={deletingId === code.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit HSN/SAC Code' : 'New HSN/SAC Code'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="hsn-code">Code *</Label>
                <Input
                  id="hsn-code"
                  value={draft.code}
                  onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. 998314"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="hsn-type">Type *</Label>
                <Select value={draft.type} onValueChange={(v) => setDraft((d) => ({ ...d, type: v as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HSN">HSN (Goods)</SelectItem>
                    <SelectItem value="SAC">SAC (Services)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="hsn-description">Description *</Label>
              <Textarea
                id="hsn-description"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Description of the goods/services"
                rows={2}
              />
            </div>

            <Separator className="my-2" />

            <h4 className="text-sm font-medium text-foreground">Tax Configuration</h4>
            <div className="space-y-1.5">
              <Label htmlFor="hsn-tax">Tax Master</Label>
              <Select value={draft.taxMasterId} onValueChange={(v) => setDraft((d) => ({ ...d, taxMasterId: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Tax Master (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None (Manual)</SelectItem>
                  {taxMasters.map((tax) => (
                    <SelectItem key={tax.id} value={tax.id}>
                      {taxLabel(tax)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}